import React, { Fragment, useMemo } from 'react';
import styles from './BarChart.module.scss';

const BarChart = ({ bars = [] }) => {
  const positiveValues = useMemo(() => bars.filter(item => item.value > 0), [bars]);
  const negativeValues = useMemo(() => bars.filter(item => item.value < 0), [bars]);

  const hasPositiveValue = positiveValues.length > 0;
  const hasNegativeValue = negativeValues.length > 0;

  const maxValue = useMemo(() => Math.max(...bars.map(item => Math.abs(item.value))), [bars]);

  const getScale = useMemo(() => {
    const range = Math.floor(maxValue);
    const items = range > 5 ? 5 : range;
    const distance = range > 5 ? range / maxValue / 5 : 1 / range;
    const values = [];
    for (let index = 0; index <= items; index++) {
      const label = `${Math.round((range > 5 ? maxValue : range) * distance * index)}%`;
      const value = range > 5 ? index * distance * 100 : index * 100 / maxValue;
      values.push({ label, value });
    }
    return values;
  }, [maxValue]);

  const getClassName = () => {
    if (hasPositiveValue && hasNegativeValue) {
      return styles.container;
    } else if (!hasPositiveValue && hasNegativeValue) {
      return styles.containerLeft;
    } else if (hasPositiveValue && !hasNegativeValue) {
      return styles.containerRight;
    }
  };

  return (
    <div className={getClassName()}>
      {hasNegativeValue && (
        <Fragment>
          <div
            className={styles.chartLeft}
            style={{
              background: `
                repeating-linear-gradient(
                  to left,
                  rgba(191, 191, 191, 0.25),
                  rgba(191, 191, 191, 0.25) 2px,
                  rgba(255, 255, 255, 0) 2px,
                  rgba(255, 255, 255, 0) ${maxValue > 5 ? Math.floor(maxValue) / maxValue / 0.05 : 100 / maxValue}%
                )
            `}}
          >
            <ul>
              {bars.map((bar, index) => (
                <Fragment key={index}>
                  {bar.value < 0 && (
                    <li
                      style={{
                        backgroundColor: bar.color,
                        color: bar.color,
                        width: `calc(${(Math.abs(bar.value) * 100) / maxValue}% - 20px)`
                      }}
                    >
                      <span
                        style={{
                          textShadow: `
                            -1px -1px 0 ${bar.color},
                            1px -1px 0 ${bar.color},
                            -1px 1px 0 ${bar.color},
                            1px 1px 0 ${bar.color}
                          `
                        }}
                      >
                        {bar.value}%
                      </span>
                    </li>
                  )}
                  {bar.value > 0 && (
                    <li key={index}></li>
                  )}
                </Fragment>
              ))}
            </ul>
          </div>
          <div className={styles.sideLeft}>
            <ul>
              {bars.map((bar, index) => (
                <li
                  key={index}
                  style={bar.value < 0 
                    ? {
                        background: `
                          linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)),
                          ${bar.color}
                        `
                      }
                    : {}
                  }
                />
              ))}
            </ul>
          </div>
        </Fragment>
      )}

      <div className={styles.legend}>
        <ul>
          {bars.map((bar, index) => (
            <li key={index} style={{ backgroundColor: bar.color }}>
              <span>{bar.name}</span>
            </li>
          ))}
        </ul>
      </div>

      {hasPositiveValue && (
        <Fragment>
          <div className={styles.side}>
            <ul>
              {bars.map((bar, index) => (
                <li
                  key={index}
                  style={bar.value > 0 
                    ? {
                        background: `
                          linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)),
                          ${bar.color}
                        `
                      }
                    : {}
                  }
                />
              ))}
            </ul>
          </div>
          <div
            className={styles.chart}
            style={{
              background: `
                repeating-linear-gradient(
                  to right,
                  rgba(191, 191, 191, 0.25),
                  rgba(191, 191, 191, 0.25) 2px,
                  rgba(255, 255, 255, 0) 2px,
                  rgba(255, 255, 255, 0) ${maxValue > 5 ? Math.floor(maxValue) / maxValue / 0.05 : 100 / maxValue}%
                )
            `}}
          >
            <ul>
              {bars.map((bar, index) => (
                <Fragment key={index}>
                  {bar.value > 0 && (
                    <li
                      style={{
                        backgroundColor: bar.color,
                        color: bar.color,
                        width: `calc(${(Math.abs(bar.value) * 100) / maxValue}% - 20px)`
                      }}
                    >
                      <span
                        style={{
                          textShadow: `
                            -1px -1px 0 ${bar.color},
                            1px -1px 0 ${bar.color},
                            -1px 1px 0 ${bar.color},
                            1px 1px 0 ${bar.color}
                          `
                        }}
                      >
                        {bar.label}%
                      </span>
                    </li>
                  )}
                  {bar.value < 0 && (
                    <li key={index}></li>
                  )}
                </Fragment>
              ))}
            </ul>
          </div>
        </Fragment>
      )}
      
      {/* // ESCALA NEGATIVA */}
      {hasNegativeValue && (
        <div className={styles.scaleLeft}>
          <ul>
            {getScale.map((scale, index) => (
              <li key={index} style={{ right: `${scale.value}%`}}>
                <span>-{scale.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* // ESCALA POSITIVA */}
      {(hasPositiveValue && !hasNegativeValue) && (
        <div className={styles.scale}>
          <ul>
            {getScale.map((scale, index) => (
              <li key={index} style={{ left: `${scale.value}%`}}>
                <span>{scale.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* // AMBAS */}
      {(hasPositiveValue && hasNegativeValue) && (
        <div className={styles.scaleRight}>
          <ul>
            {getScale.map((scale, index) => (
              <li key={index} style={{ left: `${scale.value}%`}}>
                <span>{scale.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BarChart;